import React, {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
  FC,
  ReactNode,
} from 'react';
import { BaseChip } from './BaseChip';
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form';
import {
  InputAdornment,
  ListItemButton,
  ListItemText,
  listItemTextClasses,
  MenuItem,
  TextField,
} from '@mui/material';
import { BaseChipMenu } from './components/BaseChipMenu';
import { Filter, useFormCustomContext } from '../index';

type Props = ControllerRenderProps<FieldValues, string> & {
  label: string;
  options: Filter<any>['options'];
  disabled?: boolean;
  tooltipTitle?: string | ReactNode;
  externalOnChange?: ({
    data,
    setValue,
  }: {
    data?: string | null;
    setValue?: UseFormSetValue<FieldValues>;
  }) => void;
};

export const DateChip: FC<Props> = ({
  value,
  label,
  options = [],
  name,
  tooltipTitle,
  disabled,
  externalOnChange,
}) => {
  const [selectionHappened, setSelectionHappened] = useState(false);
  const { setValue } = useFormContext();
  const { onChangeSubmit, formIsSubmittedOnFieldChange, setIsSubmitting } =
    useFormCustomContext();
  const anchorEl = useRef<HTMLElement | null>(null);
  const [isOpen, setOpen] = useState(false);

  const formatterSelectedValue = useMemo(() => {
    return value ? [`${value} Day${(value as number) > 1 ? 's' : ''}`] : null;
  }, [value]);

  const handleToggleChip = useCallback((e?: MouseEvent<HTMLElement>) => {
    setOpen((isOpen) => !isOpen);

    if (e) {
      anchorEl.current = e.currentTarget;
    }
  }, []);

  const handleCloseMenu = useCallback(
    (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      setOpen(false);

      if (formIsSubmittedOnFieldChange && selectionHappened) {
        setSelectionHappened(false);
        setIsSubmitting(true);
        setTimeout(onChangeSubmit, 0);
      }
    },
    [
      formIsSubmittedOnFieldChange,
      onChangeSubmit,
      selectionHappened,
      setIsSubmitting,
    ],
  );

  const handleSelectOption = useCallback(
    (newValue: string | null) => {
      setSelectionHappened(true);
      setValue(name, newValue);
      externalOnChange?.({ data: newValue, setValue });
    },
    [setValue, name, externalOnChange],
  );

  const handleChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectionHappened(true);
      const value = event.target.value ? event.target.value : null;
      setValue(name, value);
      externalOnChange?.({ data: value, setValue });
    },
    [setValue, name, externalOnChange],
  );

  const handleResetValues = useCallback(async () => {
    setValue(name, null);
    externalOnChange?.({ data: null, setValue });

    if (formIsSubmittedOnFieldChange) {
      setIsSubmitting(true);
      await onChangeSubmit();
    }
  }, [
    setValue,
    name,
    externalOnChange,
    formIsSubmittedOnFieldChange,
    setIsSubmitting,
    onChangeSubmit,
  ]);

  return (
    <BaseChip
      formattedSelectedValue={formatterSelectedValue}
      name={name}
      label={label}
      isOpen={isOpen}
      disabled={disabled}
      tooltipTitle={tooltipTitle}
      resetValue={handleResetValues}
      onClick={handleToggleChip}
    >
      <BaseChipMenu
        anchorEl={anchorEl?.current}
        isOpen={isOpen}
        onClose={handleCloseMenu}
      >
        {options.map((option) => {
          const { value: optionValue, secondaryLabel, label } = option;
          const isSelected = (() => {
            if (!value) {
              return false;
            } else if (typeof value === 'number') {
              return +(optionValue ?? 0) === value;
            } else {
              return value?.includes(optionValue) ?? false;
            }
          })();

          return (
            <ListItemButton
              key={optionValue}
              sx={{ py: 0 }}
              selected={isSelected}
              onClick={() => handleSelectOption(optionValue)}
            >
              <ListItemText
                primary={label}
                secondary={secondaryLabel}
                sx={{
                  [`& .${listItemTextClasses.primary}`]: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '0.9rem',
                  },
                  [`& .${listItemTextClasses.secondary}`]: {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '0.8rem',
                  },
                }}
              />
            </ListItemButton>
          );
        })}
        <MenuItem
          key="NumberSelect"
          sx={{ display: 'flex', alignItems: 'flex-end' }}
          disableRipple
        >
          <TextField
            size="small"
            onChange={handleChangeInput}
            label="Custom"
            variant="outlined"
            value={value ? +value : ''}
            sx={{ width: '15ch' }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              maxLength: 4,
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">Days</InputAdornment>
              ),
            }}
          />
        </MenuItem>
      </BaseChipMenu>
    </BaseChip>
  );
};
